import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Gift, Star, ArrowRight, Crown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { seedAPI } from '../../services/api';
import { Button } from '../../components/ui/button';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, role } = useAuth();
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      if (role === 'customer') navigate('/customer/dashboard');
      else if (role === 'staff') navigate('/staff/dashboard');
      else if (role === 'admin') navigate('/admin/dashboard');
    }
  }, [isAuthenticated, role, navigate]);

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedAPI.seed();
      alert('Demo data seeded successfully! You can now login with:\n\nAdmin: admin@salono.com / SalonO2024!\nStaff: emily@salono.com / Staff2024!');
    } catch (error) {
      console.error('Seed error:', error);
    } finally {
      setSeeding(false);
    }
  };

  const features = [
    { icon: Star, title: 'Earn Points', desc: 'Get 10 points for every $1 spent' },
    { icon: Crown, title: 'Unlock Tiers', desc: 'Bronze, Silver, Gold & Platinum' },
    { icon: Gift, title: 'Redeem Rewards', desc: 'Free services & exclusive perks' },
  ];

  return (
    <div className="min-h-screen bg-[#FFFFF0]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1920&q=80"
            alt="Luxury Salon"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#4A154B]/70 via-[#4A154B]/50 to-[#FFFFF0]" />
        </div>

        {/* Header */}
        <header className="relative z-10 px-6 py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="font-serif text-3xl text-white">Salon O</h1>
              <p className="text-[#C9A689] text-xs tracking-[0.3em] uppercase">Luxury Rewards</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/staff/login')}
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="staff-portal-btn"
              >
                Staff
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/login')}
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="admin-portal-btn"
              >
                Admin
              </Button>
            </div>
          </div>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex items-center justify-center px-6 pb-20">
          <div className="text-center max-w-2xl animate-fadeIn">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#C9A689]" />
              <span className="text-white/90 text-sm">Exclusive Loyalty Program</span>
            </div>
            
            <h2 className="font-serif text-4xl md:text-6xl text-white leading-tight mb-6">
              Experience Beauty,<br />
              <span className="text-[#C9A689]">Earn Rewards</span>
            </h2>
            
            <p className="text-white/80 text-lg mb-10 max-w-lg mx-auto leading-relaxed">
              Join our luxury rewards program and turn every salon visit into 
              exclusive perks, discounts, and complimentary services.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/auth')}
                className="btn-primary text-lg px-10 py-4 flex items-center justify-center gap-2"
                data-testid="join-rewards-btn"
              >
                Join Rewards
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="btn-outline border-white text-white hover:bg-white hover:text-[#4A154B] px-10 py-4"
                data-testid="sign-in-btn"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#FFFFF0] to-[#F5E6D3]/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-slideUp">
            <p className="text-[#C9A689] tracking-[0.2em] uppercase text-sm mb-3">How It Works</p>
            <h3 className="font-serif text-3xl md:text-4xl text-[#4A154B]">Your Path to Luxury</h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="glass rounded-2xl p-8 text-center card-hover animate-slideUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-serif text-xl text-[#4A154B] mb-3">{feature.title}</h4>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers Section */}
      <section className="py-20 px-6 bg-[#FFFFF0]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C9A689] tracking-[0.2em] uppercase text-sm mb-3">Membership Tiers</p>
            <h3 className="font-serif text-3xl md:text-4xl text-[#4A154B]">Unlock Exclusive Benefits</h3>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Bronze', points: '0-199', mult: '1x', color: 'tier-bronze' },
              { name: 'Silver', points: '200-499', mult: '1.25x', color: 'tier-silver' },
              { name: 'Gold', points: '500-999', mult: '1.5x', color: 'tier-gold' },
              { name: 'Platinum', points: '1000+', mult: '2x', color: 'tier-platinum' },
            ].map((tier, index) => (
              <div key={tier.name} className="glass rounded-2xl p-6 text-center card-hover">
                <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4 ${tier.color}`}>
                  {tier.name}
                </div>
                <p className="text-gray-600 text-sm mb-2">Lifetime Points</p>
                <p className="font-serif text-2xl text-[#4A154B] mb-4">{tier.points}</p>
                <p className="text-[#C9A689] font-semibold">{tier.mult} Points</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 gradient-plum">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-serif text-3xl md:text-4xl text-white mb-6">
            Ready to Start Earning?
          </h3>
          <p className="text-white/80 text-lg mb-10 max-w-xl mx-auto">
            Join thousands of members enjoying exclusive rewards at Salon O.
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="bg-[#C9A689] text-white font-medium px-10 py-4 rounded-full text-lg hover:bg-white hover:text-[#4A154B] transition-all duration-300"
            data-testid="join-now-btn"
          >
            Join Now — It's Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 bg-[#2C2C2C] text-white/60">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h4 className="font-serif text-xl text-white">Salon O</h4>
            <p className="text-sm">Luxury Rewards Program</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleSeedData}
              disabled={seeding}
              className="text-sm text-white/40 hover:text-[#C9A689] transition-colors"
              data-testid="seed-data-btn"
            >
              {seeding ? 'Seeding...' : 'Initialize Demo Data'}
            </button>
          </div>
          <p className="text-sm">© 2024 Salon O. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
